var __rest = this && this.__rest || function (s, e) {
  var t = {};
  for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0) t[p] = s[p];
  if (s != null && typeof Object.getOwnPropertySymbols === "function") for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
    if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i])) t[p[i]] = s[p[i]];
  }
  return t;
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { forwardRef, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Pressable, View } from 'react-native';
import { StateConfigContext } from '../../providers';
import { checkIsPlatform, getTestId } from '../../utils';
import { Card } from '../card';
import { InfoTag } from '../info-tag';
import { Typography } from '../typography';
import { getRadioProps, getRadioStyles, getRadioTextProps, getRadioWrapperStyles } from './RadioStyles';
export var Radio = forwardRef((props, ref) => {
  var stateContext = useContext(StateConfigContext);
  var radioProps = getRadioProps(props, stateContext);
  var stylesBasedOnState = useRef();
  var label = radioProps.label,
    description = radioProps.description,
    controlState = radioProps.controlState,
    disabled = radioProps.disabled,
    checked = radioProps.checked,
    _onPress = radioProps.onPress,
    value = radioProps.value,
    style = radioProps.style,
    _radioProps$testID = radioProps.testID,
    testID = _radioProps$testID === void 0 ? 'Radio' : _radioProps$testID,
    showAsCard = radioProps.showAsCard,
    isInfoTagOnLabel = radioProps.isInfoTagOnLabel,
    showInfoTagOnCard = radioProps.showInfoTagOnCard,
    infoTagOnLabelProps = radioProps.infoTagOnLabelProps,
    infoTagProps = radioProps.infoTagProps,
    theme = radioProps.theme,
    wrapperStyle = radioProps.wrapperStyle,
    _radioProps$showDescr = radioProps.showDescription,
    _showDescription = _radioProps$showDescr === void 0 ? true : _radioProps$showDescr,
    restProps = __rest(radioProps, ["label", "description", "controlState", "disabled", "checked", "onPress", "value", "style", "testID", "showAsCard", "isInfoTagOnLabel", "showInfoTagOnCard", "infoTagOnLabelProps", "infoTagProps", "theme", "wrapperStyle", "showDescription"]);
  var _useState = useState(_showDescription),
    showDescription = _useState[0],
    setShowDescription = _useState[1];
  useEffect(() => {
    setShowDescription(!!description && _showDescription);
  }, [description, _showDescription]);
  var getRadioPressableStyles = useCallback(event => {
    var styles = getRadioStyles(radioProps, event);
    stylesBasedOnState.current = styles;
    return [styles.container, style];
  }, [radioProps]);
  var hasLabelOrDesc = label || description;
  return _jsx(WrapperComponent, Object.assign({
    showAsCard: showAsCard,
    infoTagProps: infoTagProps,
    showInfoTagOnCard: showInfoTagOnCard,
    checked: checked,
    disabled: disabled || controlState == 'disabled',
    theme: theme,
    style: wrapperStyle
  }, {
    children: _jsx(Pressable, Object.assign({
      ref: ref,
      accessibilityRole: "radio",
      style: getRadioPressableStyles,
      disabled: disabled || controlState == 'disabled',
      accessibilityState: {
        checked: checked,
        disabled: disabled || controlState == 'disabled'
      }
    }, getTestId(testID), checkIsPlatform('web') && {
      'aria-checked': checked
    }, restProps, {
      onPress: event => {
        var data = {
          event,
          checked,
          value,
          label,
          description
        };
        _onPress === null || _onPress === void 0 ? void 0 : _onPress(data);
      }
    }, {
      children: event => {
        var textContainerProps = getRadioTextProps(event, radioProps);
        return _jsxs(_Fragment, {
          children: [_jsx(View, Object.assign({
            style: stylesBasedOnState.current.radioCircle
          }, getTestId(testID, 'radioCircle'), {
            children: checked && _jsx(View, Object.assign({
              style: stylesBasedOnState.current.radioSelected
            }, getTestId(testID, 'radioSelected')))
          })), hasLabelOrDesc && _jsxs(View, Object.assign({
            style: stylesBasedOnState.current.radioTextContainer
          }, getTestId(testID, 'hasLabelOrDesc'), {
            children: [!!label && _jsxs(View, Object.assign({
              style: {
                flexDirection: 'row'
              }
            }, {
              children: [_jsx(Typography, Object.assign({
                text: label,
                type: textContainerProps.labelFontType,
                color: textContainerProps.labelColor
              }, getTestId(testID, 'label'))), isInfoTagOnLabel && _jsx(InfoTag, Object.assign({}, infoTagOnLabelProps, {
                disabled: infoTagOnLabelProps === null || infoTagOnLabelProps === void 0 ? void 0 : infoTagOnLabelProps.disabled,
                style: [infoTagOnLabelProps === null || infoTagOnLabelProps === void 0 ? void 0 : infoTagOnLabelProps.style, {
                  marginLeft: 8
                }],
                theme: theme
              }, getTestId(testID, 'Infotag')))]
            })), showDescription && !!description && _jsx(Typography, Object.assign({
              text: description,
              type: textContainerProps.descriptionFontType,
              color: textContainerProps.descriptionColor,
              style: !!label && !!description && stylesBasedOnState.current.radioText
            }, getTestId(testID, 'description')))]
          }))]
        });
      }
    }))
  }));
});
var WrapperComponent = props => {
  var showAsCard = props.showAsCard,
    infoTagProps = props.infoTagProps,
    showInfoTagOnCard = props.showInfoTagOnCard,
    children = props.children,
    disabled = props.disabled,
    theme = props.theme,
    _props$testID = props.testID,
    testID = _props$testID === void 0 ? 'RadioWrapper' : _props$testID,
    style = props.style;
  var styles = getRadioWrapperStyles(props);
  return showAsCard ? _jsxs(View, Object.assign({
    style: [styles.containerStyles, style]
  }, getTestId(testID), {
    children: [showInfoTagOnCard && _jsx(InfoTag, Object.assign({}, infoTagProps, {
      disabled: disabled,
      style: !disabled && styles.selectedBorderColor,
      theme: theme
    }, getTestId(testID, 'Infotag'))), _jsx(Card, Object.assign({
      showShadow: false,
      noPadding: true,
      style: [styles.cardStyles, !disabled && styles.selectedBorderColor],
      theme: theme
    }, getTestId(testID, 'Card'), {
      children: children
    }))]
  })) : _jsx(_Fragment, {
    children: children
  });
};